import PagedList from "models/accessors/pagedList";
import LoanApplication from "../models/loan/loanApplication";
import { DraftApplicationSummary } from "../models/loan/draftApplicationSummary";
import axiosRequestUtility from "../utilities/AxiosRequestUtility";
import { CollateralEquityTypes, DocumentTypeMapper, FixedAssetType, IdDocumentSde } from "../shared/Enums";
import LoanDocument from "models/loanDocument/loanDocument";
import AddDocumentsRequest from "models/addDocumentsRequest/AddDocumentsRequest";
import DownloadDocumentResponse from "models/downloadDocumentResponse/DownloadDocumentResponse"
import DocumentRequest from "models/documentData/DocumentRequest";
import FSA2211 from "models/fsa2211Form/FSA2211";
import LoanApplicationFilterCriteria from "models/accessors/LoanApplicationFilterCriteria";
import globalUtility from "utilities/GlobalUtility";
import IdDocumentUpload from "models/account/customer/idDocumentUpload/idDocumentUpload";
import CustomerAccountVerificationDocument from "models/account/customer/customerAccountVerificationDocument";
import publicAxiosRequestUtility from "utilities/PublicAxiosRequestUtility";
import EntityApplicantExtraData from "models/applicantForm/entityApplicantExtraData";
import toastUtility from 'utilities/ToastUtility';

class LoanApplicationService {
  public GetLoanApplications(
    filterCriteria: LoanApplicationFilterCriteria
  ): Promise<PagedList<LoanApplication>> {
    return axiosRequestUtility.Request({
      method: 'GET',
      url: `LoanApplications`,
      params:
      {
        'pageNumber': filterCriteria.pageNumber,
        'listType': filterCriteria.loanListType,
        'loanType': filterCriteria.loanTypeFilter.join(','),
        'scorecardLevel': filterCriteria.scorecardLevelFilter.join(','),
        'loanStatus': filterCriteria.loanStatusFilter.join(','),
        'loanOfficer': filterCriteria.loanOfficerFilter.join(','),
        'referenceNumber': filterCriteria.submittedReferenceNumberFilter,
        'submittedStartDate': filterCriteria.submittedStartDateFilter,
        'submittedEndDate': filterCriteria.submittedEndDateFilter,
      }
    })
      .then(response => {
        let pagedLoanApplications = response.data;

        pagedLoanApplications.items.forEach((la: LoanApplication) => {
          la = this.MapLoanApplicationParts(la);
        })

        return pagedLoanApplications;
      })
      .catch(error => {
        console.log(error);
        alert('Error loading loan applications. Please try again.')
        return [];
      })
  }

  public ExportLoanApplications(
    listType: 'active' | 'closed',
    loanTypeFilter: number[],
    scorecardLevelFilter: number[],
    loanStatusFilter: number[],
    loanOfficerFilter: number[],
    submittedStartDateFilter?: Date,
    submittedEndDateFilter?: Date
  ): Promise<boolean> {
    return axiosRequestUtility.Request({
      method: 'GET',
      url: `ExportLoanApplications`,
      params:
      {
        'listType': listType,
        'loanType': loanTypeFilter.join(','),
        'scorecardLevel': scorecardLevelFilter.join(','),
        'loanStatus': loanStatusFilter.join(','),
        'loanOfficer': loanOfficerFilter.join(','),
        'submittedStartDate': submittedStartDateFilter,
        'submittedEndDate': submittedEndDateFilter,
      }
    })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${listType}-loan-list-${new Date().getTime()}.csv`); //or any other extension
        document.body.appendChild(link);
        link.click();

        return true;
      })
      .catch(error => {
        console.log(error);
        alert('Error exporting loan applications. Please try again.')
        return false;
      })
  }

  public SubmitLoanApplication(loanApplication: LoanApplication): Promise<number> {
    if (loanApplication.entityApplicant != undefined && loanApplication.entityApplicant.extraDataObject != undefined) {
      loanApplication.entityApplicant.extraData = JSON.stringify(loanApplication.entityApplicant.extraDataObject);
    }

    return axiosRequestUtility.Request({
      method: 'POST',
      url: 'SubmitApplication',
      data: loanApplication
    })
      .then(response => {
        return +response.data;
      })
      .catch(error => {
        console.log(error);
        alert('Error submitting loan application. Please try again.')
        return 0;
      })
  }

  public SubmitLoanApplicationPublic(loanApplication: LoanApplication): Promise<number> {
    if (loanApplication.entityApplicant != undefined && loanApplication.entityApplicant.extraDataObject != undefined) {
      loanApplication.entityApplicant.extraData = JSON.stringify(loanApplication.entityApplicant.extraDataObject);
    }

    return publicAxiosRequestUtility.Request({
      method: 'POST',
      url: 'SubmitApplicationPublic',
      data: loanApplication
    })
      .then(response => {
        return +response.data;
      })
      .catch(error => {
        console.log(error);
        alert('Error submitting loan application. Please try again.')
        return 0;
      })
  }

  public GetLoanApplicationDetails(loanId: number): Promise<LoanApplication> {
    return axiosRequestUtility.Request({
      method: 'GET',
      url: `LoanDetails`,
      params:
      {
        'loanId': loanId
      }
    })
      .then(response => {
        return this.MapLoanApplicationParts(response.data as LoanApplication);
      })
      .catch(error => {
        console.log(error);
        alert('Error loading loan application details. Please try again.')
        return {} as LoanApplication;
      })
  }

  public GetApplicationPrefill(): Promise<LoanApplication | null> {
    return axiosRequestUtility.Request({
      method: 'GET',
      url: 'ApplicationPrefill'
    })
      .then(response => {
        // 204 = no eligible prior application
        if (response.status === 204 || !response.data) return null;
        return this.MapLoanApplicationParts(response.data as LoanApplication);
      })
      .catch(error => {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          // Session expired or unauthorized — auth flow handles these
          console.log('ApplicationPrefill unauthorized:', error);
        } else {
          toastUtility.showErrorToast('Unable to load prior application data. You can continue without pre-fill.');
          console.log(error);
        }
        return null;
      });
  }

  private MapLoanApplicationParts(loanApplication: LoanApplication) {
    if (loanApplication.entityApplicant != undefined && loanApplication.entityApplicant.extraData != undefined && loanApplication.entityApplicant.extraData.length > 0) {
      loanApplication.entityApplicant.extraDataObject = JSON.parse(loanApplication.entityApplicant.extraData) as EntityApplicantExtraData;
    }

    loanApplication.fixedAssetTypes = [];
    loanApplication.collateralEquityTypes = [];

    // Fixed Asset Types
    if (loanApplication.fixedAssetEquipment !== undefined) {
      loanApplication.fixedAssetTypes.push(FixedAssetType.Equipment);
    }

    if (loanApplication.fixedAssetLivestock !== undefined && loanApplication.fixedAssetLivestock.length > 0) {
      loanApplication.fixedAssetTypes.push(FixedAssetType.Livestock);
    }

    if (loanApplication.fixedAssetRealEstate !== undefined) {
      loanApplication.fixedAssetTypes.push(FixedAssetType.RealEstate);
    }

    // Collateral Equity Types
    if (loanApplication.collateralEquityCashDown !== undefined) {
      loanApplication.collateralEquityTypes.push(CollateralEquityTypes.CashDown);
    }

    if (loanApplication.collateralEquityTrade !== undefined) {
      loanApplication.collateralEquityTypes.push(CollateralEquityTypes.Trade);
    }

    if (loanApplication.collateralEquityOtherCollateral !== undefined && loanApplication.collateralEquityOtherCollateral.length > 0) {
      loanApplication.collateralEquityTypes.push(CollateralEquityTypes.OtherCollateral);
    }

    if (loanApplication.loanProductionLineOfCredit !== undefined &&
        loanApplication.loanProductionLineOfCredit.cashFlowData) {
      loanApplication.loanProductionLineOfCredit.cashFlow = JSON.parse(loanApplication.loanProductionLineOfCredit.cashFlowData);
    }

    return loanApplication;
  }

  public AddLoanDocument(loanDocuments: AddDocumentsRequest): Promise<LoanDocument[]> {

    var data = new FormData();
    var filecount = 1;
    var date = new Date();
    loanDocuments.data.note = "";

    var datetag = (date.getMonth() + 1).toString() + date.getDate().toString() + date.getFullYear().toString() + date.getHours().toString() + date.getMinutes().toString();
    loanDocuments.documents.forEach(file => {
      var newFile = new File([file], `${DocumentTypeMapper[loanDocuments.data.type ?? 0].replace(" ", "_")}_${(loanDocuments.data.year ?? '').toString()}_${datetag}_${filecount.toString()}${file.name.slice(file.name.lastIndexOf('.'))}`)
      filecount++;
      data.append("files[]", newFile);
    })

    data.append("documentdata", JSON.stringify(loanDocuments.data));

    return axiosRequestUtility.Request({
      method: 'POST',
      url: `AddLoanDocuments`,
      data: data
    })
      .then(response => {
        return response.data as LoanDocument[];
      })
      .catch(error => {
        alert('Error adding document to loan application. Please try again.')
        return {} as LoanDocument[];
      })
  }

  public DeleteLoanDocument(loanDocumentId: number): Promise<boolean> {
    return axiosRequestUtility.Request({
      method: 'DELETE',
      url: `DeleteLoanDocument`,
      params:
      {
        'loanDocumentId': loanDocumentId
      }
    })
      .then(response => {
        return response.data;
      })
      .catch(error => {
        alert('Error deleting document from loan application. Please try again.')
        return false;
      })
  }

  public DownloadLoanDocument(loanDocumentId: number): Promise<DownloadDocumentResponse> {
    return axiosRequestUtility.Request({
      method: 'GET',
      responseType: 'arraybuffer',
      url: `DownloadLoanDocument`,
      params:
      {
        'loanDocumentId': loanDocumentId
      }
    })
      .then(response => {
        return globalUtility.CreateFileDownloadResponse(response);
      })
      .catch(error => {
        alert('Error downloading document from loan application. Please try again.')
        return {} as DownloadDocumentResponse;
      })
  }

  public FindOpenDocumentRequests(customerAccountId: number): Promise<DocumentRequest[]> {
    return axiosRequestUtility.Request({
      method: 'POST',
      responseType: 'array',
      url: 'FindOpenDocumentRequests',
      params:
      {
        'customerAccountId': customerAccountId
      }
    })
      .then(response => {
        return response.data as DocumentRequest[];
      })
      .catch(error => {
        alert('Error getting open Document Requests from loan application. Please try again.')
        return [] as DocumentRequest[];
      })
  }

  public AddIdDocument(idDocumentUpload: IdDocumentUpload): Promise<CustomerAccountVerificationDocument> {
    var data = new FormData();

    idDocumentUpload.documentData.forEach(d => {
      var newFile = new File([d.document], (d.documentSide === IdDocumentSde.Front ? `front${d.document.name.slice(d.document.name.lastIndexOf('.'))}` : `back${d.document.name.slice(d.document.name.lastIndexOf('.'))}`));
      data.append("files[]", newFile);
    })

    data.append('idDocumentUpload', JSON.stringify(idDocumentUpload));

    return axiosRequestUtility.Request({
      method: 'POST',
      url: `AddIdDocuments`,
      data: data
    })
      .then(response => {
        return response.data as CustomerAccountVerificationDocument;
      })
      .catch(error => {
        alert('Error adding ID documents. Please try again.')
        return {} as CustomerAccountVerificationDocument;
      })
  }

  public SaveFSA2211(FormData: FSA2211): Promise<boolean> {
    return axiosRequestUtility.Request({
      method: 'POST',
      url: `SaveFSA2211`,
      data: FormData
    })
      .then(response => {
        return true;
      })
      .catch(error => {
        alert('Error saving FSA2211. Please try again.')
        return false;
      })
  }

  public GetCurrentFSA2211RevisionVersion(): Promise<string> {
    return axiosRequestUtility.Request({
      method: 'GET',
      url: `GetCurrentFSA2211RevisionVersion`
    })
      .then(response => {
        return response.data;
      })
      .catch(error => {
        alert('Error getting current revision version. Please try again.')
        return "";
      })
  }

  public GenerateFSA2211Pdf(FormData: FSA2211): Promise<DownloadDocumentResponse> {
    return axiosRequestUtility.Request({
      method: 'POST',
      responseType: 'arraybuffer',
      url: `GenerateFSA2211Pdf`,
      data: FormData
    })
      .then(response => {
        return globalUtility.CreateFileDownloadResponse(response);
      })
      .catch(error => {
        alert('Error exporting FSA2211. Please try again.')
        return {} as DownloadDocumentResponse;
      })
  }

  public DownloadFSA2211Pdf(loanId: number): Promise<DownloadDocumentResponse> {
    return axiosRequestUtility.Request({
      method: 'POST',
      responseType: 'arraybuffer',
      url: `DownloadFSA2211`,
      data: loanId
    })
      .then(response => {
        return globalUtility.CreateFileDownloadResponse(response);
      })
      .catch(error => {
        alert('Error downloading FSA2211. Please try again.')
        return {} as DownloadDocumentResponse;
      })
  }

  public AcceptLoanOffer(loanId: number, loanOfferId: number): Promise<boolean> {
    return axiosRequestUtility.Request({
      method: 'PUT',
      url: 'AcceptLoanOffer',
      params:
      {
        'loanId': loanId,
        'loanOfferId': loanOfferId
      }
    })
      .then(response => {
        return true;
      })
      .catch(error => {
        console.log(error);
        alert('Error accepting loan offer. Please try again.')
        return false;
      })
  }

  public RejectLoanOffer(loanId: number, loanOfferId: number): Promise<boolean> {
    return axiosRequestUtility.Request({
      method: 'PUT',
      url: 'RejectLoanOffer',
      params:
      {
        'loanId': loanId,
        'loanOfferId': loanOfferId
      }
    })
      .then(response => {
        return true;
      })
      .catch(error => {
        console.log(error);
        alert('Error rejecting loan offer. Please try again.')
        return false;
      })
  }

  public SaveDraftPublic(appData: Partial<LoanApplication>, existingToken?: string): Promise<string> {
    return publicAxiosRequestUtility.Request({
      method: 'POST',
      url: 'SaveDraftPublic',
      data: { applicationData: JSON.stringify(appData), accessToken: existingToken }
    })
      .then(response => {
        return response.data as string;
      })
      .catch(error => {
        console.log(error);
        toastUtility.showErrorToast('Unable to save draft. Your progress may not be saved.');
        throw error;
      });
  }

  public GetDraftPublic(token: string): Promise<LoanApplication | null> {
    return publicAxiosRequestUtility.Request({
      method: 'GET',
      url: 'GetDraftPublic',
      params: { token }
    })
      .then(response => {
        if (response.status === 204 || !response.data) return null;
        return this.MapLoanApplicationParts(response.data as LoanApplication);
      })
      .catch(error => {
        console.log(error);
        return null;
      });
  }

  public SendDraftMagicLink(token: string, email: string, sqId?: string): Promise<boolean> {
    return publicAxiosRequestUtility.Request({
      method: 'POST',
      url: 'SendDraftMagicLinkPublic',
      data: { token, email, sqId }
    })
      .then(() => true)
      .catch(error => {
        console.log(error);
        toastUtility.showErrorToast('Unable to send resume link. Please try again.');
        return false;
      });
  }

  public DeleteDraftPublic(token: string): Promise<void> {
    return publicAxiosRequestUtility.Request({
      method: 'DELETE',
      url: 'DeleteDraftPublic',
      params: { token }
    })
      .then(() => { })
      .catch(error => {
        console.log(error);
      });
  }

  public SaveDraft(appData: LoanApplication, existingToken?: string): Promise<{ token: string; wasEvicted: boolean }> {
    return axiosRequestUtility.Request({
      method: 'POST',
      url: 'SaveDraft',
      data: { applicationData: JSON.stringify(appData), existingToken }
    })
      .then(response => {
        const token = response.data as string;
        const wasEvicted = response.headers['x-draft-evicted'] === 'true';
        return { token, wasEvicted };
      })
      .catch(error => {
        console.log(error);
        toastUtility.showErrorToast('Unable to save draft. Your progress may not be saved.');
        throw error;
      });
  }

  public GetDraftByToken(token: string): Promise<LoanApplication | null> {
    return axiosRequestUtility.Request({
      method: 'GET',
      url: `GetDraftByToken?token=${token}`
    })
      .then(response => {
        if (response.status === 204 || !response.data) return null;
        return this.MapLoanApplicationParts(response.data as LoanApplication);
      })
      .catch(error => {
        console.log(error);
        return null;
      });
  }

  public GetDraft(): Promise<LoanApplication | null> {
    return axiosRequestUtility.Request({
      method: 'GET',
      url: 'GetDraft'
    })
      .then(response => {
        if (response.status === 204 || !response.data) return null;
        return this.MapLoanApplicationParts(response.data as LoanApplication);
      })
      .catch(error => {
        console.log(error);
        return null;
      });
  }

  public DeleteDraft(token: string): Promise<void> {
    return axiosRequestUtility.Request({
      method: 'DELETE',
      url: `DeleteDraft?token=${token}`
    })
      .then(() => { })
      .catch(error => {
        console.log(error);
      });
  }

  public RestoreDraft(token: string): Promise<void> {
    return axiosRequestUtility.Request({
      method: 'PUT',
      url: `RestoreDraft?token=${token}`
    })
      .then(() => { });
  }

  public ClaimDraft(anonymousToken: string): Promise<string | null> {
    return axiosRequestUtility.Request({
      method: 'POST',
      url: 'ClaimDraft',
      data: { anonymousToken }
    })
      .then((response: any) => response?.data?.newToken ?? null)
      .catch(() => null);
  }

  public async ClaimDrafts(tokens: string[]): Promise<void> {
    for (const token of tokens) {
      await this.ClaimDraft(token);
    }
  }

  public GetAllDrafts(): Promise<DraftApplicationSummary[]> {
    return axiosRequestUtility.Request({
      method: 'GET',
      url: 'GetAllDrafts'
    })
      .then(response => {
        return response.data as DraftApplicationSummary[];
      })
      .catch(error => {
        console.log(error);
        return [] as DraftApplicationSummary[];
      });
  }

  public GetAllDraftsPublic(tokens: string[]): Promise<DraftApplicationSummary[]> {
    return publicAxiosRequestUtility.Request({
      method: 'POST',
      url: 'GetAllDraftsPublic',
      data: { tokens }
    })
      .then(response => {
        return response.data as DraftApplicationSummary[];
      })
      .catch(error => {
        console.log(error);
        return [] as DraftApplicationSummary[];
      });
  }

}

const loanApplicationService = new LoanApplicationService();
export default loanApplicationService;